import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability, AppUpdateInfo } from '@capawesome/capacitor-app-update';
import { TranslateService } from '@ngx-translate/core';
import { UIDialogConfirmService } from '@ui/components';
import { EnvironmentService } from '@util/environment';
import { filter, from, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SharedAppUpdateService {
  private readonly translateService = inject(TranslateService);
  private readonly confirmService = inject(UIDialogConfirmService);
  private readonly environmentService = inject(EnvironmentService);

  checkAppStoreUpdates(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    this.getAvailableUpdate$()
      .pipe(
        filter(Boolean),
        switchMap((): Observable<boolean> => this.openUpdateDialog$()),
        filter(Boolean),
      )
      .subscribe(() => {
        this.openAppStore();
      });
  }

  private getAvailableUpdate$(): Observable<boolean> {
    return from(AppUpdate.getAppUpdateInfo()).pipe(
      map(
        (appUpdateInfo: AppUpdateInfo) => appUpdateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE,
      ),
      catchError(() => of(false)),
    );
  }

  private openUpdateDialog$(): Observable<boolean> {
    return this.confirmService.open$({
      title: this.translateService.instant('appUpdate.title'),
      description: this.translateService.instant('appUpdate.description'),
      confirmButtonText: this.translateService.instant('appUpdate.update'),
      cancelButtonText: this.translateService.instant('appUpdate.later'),
    });
  }

  private openAppStore(): void {
    AppUpdate.openAppStore({
      appId: this.environmentService.environment.appleAppId,
    });
  }
}
